import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useLatest } from 'ahooks';
import { useWebsocket } from '@zg-rentals/react-hooks';

import type { ReactNode } from 'react';

type MessageListener = (event: MessageEvent) => void;

type RentalManagerWebSocketContextValue = {
  addMessageListener: (listener: MessageListener) => () => void;
};

const RentalManagerWebSocketContext = createContext<RentalManagerWebSocketContextValue | undefined>(undefined);

const getSocketUrl = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  // No rental-manager-api websocket backend to connect to locally; skip connecting instead of
  // erroring, matching the retired setup's dev-mode mock.
  if (process.env.NODE_ENV === 'development') return undefined;

  return `wss://${window.location.hostname}/rental-manager-api/ws/services`;
};

/**
 * Owns the single rental-manager-api websocket connection and fans its messages out to any number
 * of `useRentalManagerWebSocketMessage` subscribers, replacing the `window.rentalManagerApiWebSocket`
 * global.
 */
export const RentalManagerWebSocketProvider = ({ children }: { children: ReactNode }) => {
  const listenersRef = useRef<Set<MessageListener>>(new Set());

  useWebsocket(getSocketUrl(), {
    onMessage: (event) => {
      listenersRef.current.forEach((listener) => listener(event));
    },
  });

  const contextValue = useMemo<RentalManagerWebSocketContextValue>(
    () => ({
      addMessageListener: (listener) => {
        listenersRef.current.add(listener);
        return () => listenersRef.current.delete(listener);
      },
    }),
    [],
  );

  return (
    <RentalManagerWebSocketContext.Provider value={contextValue}>{children}</RentalManagerWebSocketContext.Provider>
  );
};

/**
 * The shared rental-manager-api websocket connection, for a caller that needs more than
 * `useRentalManagerWebSocketMessage`'s managed subscription - e.g. `zrm-header-navigation`'s
 * `NotificationWebsocketProvider`, which only subscribes conditionally via its
 * `webSocketSource` prop.
 */
export const useRentalManagerWebSocketContext = (): RentalManagerWebSocketContextValue => {
  const context = useContext(RentalManagerWebSocketContext);
  if (!context) {
    throw new Error('useRentalManagerWebSocketContext must be used within a RentalManagerWebSocketProvider');
  }

  return context;
};

/** Subscribes to messages from the shared rental-manager-api websocket connection while mounted. */
export const useRentalManagerWebSocketMessage = (onMessage: MessageListener) => {
  const { addMessageListener } = useRentalManagerWebSocketContext();
  const onMessageRef = useLatest(onMessage);

  useEffect(() => addMessageListener((event) => onMessageRef.current?.(event)), [addMessageListener, onMessageRef]);
};
